// SETTINGS V2 — pass-type selector for the mint dialog (DEMO_USER_TYPE_SPEC F1).
//
// Two radio cards: guest technician (machine-scoped, the existing default) vs
// demo visitor (full worker access, demo companies only). Rendered ONLY for
// callers holding Users:Admin — the same gate the server enforces on
// POST /v1/users/guest-passes/demo — so a Manager sees the dialog exactly as it
// was before this feature existed.
//
// Radio semantics are real (role="radiogroup" + arrow-key/space handling comes
// free from native inputs), which matters because this control changes which
// endpoint the form posts to and how much privilege the resulting principal has.
// Composes SField chrome for label parity with the rest of the form.
import React from "react";

import { SField } from "../atoms";

export type PassType = "guest" | "demo";

export interface PassTypeSelectorV2Props {
  label: string;
  value: PassType;
  onChange: (v: PassType) => void;
  guestLabel: string;
  guestHint: string;
  demoLabel: string;
  demoHint: string;
  disabled?: boolean;
  /** Stack the cards on phone. */
  stacked?: boolean;
}

const PassTypeSelectorV2: React.FC<PassTypeSelectorV2Props> = ({
  label,
  value,
  onChange,
  guestLabel,
  guestHint,
  demoLabel,
  demoHint,
  disabled,
  stacked,
}) => {
  const card = (option: PassType, title: string, hint: string) => {
    const selected = value === option;
    return (
      <label
        key={option}
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 9,
          padding: "10px 12px",
          borderRadius: 10,
          cursor: disabled ? "not-allowed" : "pointer",
          minWidth: 0,
          border: `1px solid ${selected ? "var(--mod-accent)" : "var(--border)"}`,
          background: selected ? "var(--mod-accent-soft)" : "var(--card)",
          opacity: disabled ? 0.6 : 1,
          transition: "border-color 0.15s ease, background 0.15s ease",
        }}
      >
        <input
          type="radio"
          name="sv2-pass-type"
          checked={selected}
          disabled={disabled}
          onChange={() => onChange(option)}
          style={{
            marginTop: 2,
            accentColor: "var(--mod-accent)",
            flexShrink: 0,
            cursor: disabled ? "not-allowed" : "pointer",
          }}
        />
        <span style={{ minWidth: 0 }}>
          <span
            style={{
              display: "block",
              fontSize: 13,
              fontWeight: 600,
              color: "var(--ink)",
              lineHeight: 1.35,
            }}
          >
            {title}
          </span>
          <span
            style={{
              display: "block",
              fontSize: 11.5,
              color: "var(--muted)",
              lineHeight: 1.45,
              marginTop: 2,
            }}
          >
            {hint}
          </span>
        </span>
      </label>
    );
  };

  return (
    <SField label={label}>
      <div
        role="radiogroup"
        aria-label={label}
        style={{
          display: "grid",
          gridTemplateColumns: stacked ? "1fr" : "1fr 1fr",
          gap: 8,
        }}
      >
        {card("guest", guestLabel, guestHint)}
        {card("demo", demoLabel, demoHint)}
      </div>
    </SField>
  );
};

export default PassTypeSelectorV2;
